import {
  IUnderwritingCoverage,
  IUnderwritingQuote,
  RaterMode,
  RaterPolicyDetails,
  RaterUserQuotes,
  SaveAnswerInput,
  Submission,
} from '@converge/graphql';
import {
  RiskLevelType,
  UnderwritingEnterpriseFusionScoreType,
  UnderwritingEnterpriseFusionScoreTypeId,
  UnderwritingScheduleRatingType,
  UnderwritingScheduleRatingTypeId,
} from '@converge/data';

import { CoverageErrorDetails } from './rater.socket.output.types';

export * from './rater.socket.input.types';
export * from './rater.socket.output.types';

export enum RaterColumnCount {
  Expanded = 23,
  Contracted = 13,
}

export interface IRaterProps {
  mode: RaterMode;
  isEditable?: boolean;
}

export interface IRaterContext {
  options: Readonly<IRaterProps>;
}

export interface IRaterSocketMessage {
  message: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  id: number;
}

export interface IRaterScheduleRatingProps {
  ratingTypeId: UnderwritingScheduleRatingTypeId;
  ratingType: UnderwritingScheduleRatingType;
  minimum: number;
  maximum: number;
  selectedRating: number;
  notes: string;
}

export interface IRaterContextState extends IRaterProps {
  isEditable: boolean;
  options: Readonly<IRaterProps>;
  userQuotes: RaterUserQuotes;
  reasonQuoteHasChanged?: string;
  quote: IUnderwritingQuote;
  changedFields: string[];
  lastReceivedMessageAt?: Date;
  submission: Submission;
  policy?: RaterPolicyDetails;
  messagesToSend: IRaterSocketMessage[];
  messagesSent: IRaterSocketMessage[];
  messagesReceived: IRaterSocketMessage[];
  lastMessageId: number;
  isRequestingRatingUpdate: boolean;
  expandNotesForUuid?: string;
  showExpandedView: boolean;
  columnCount: RaterColumnCount;
  enterpriseRatingFusionScore?: number;
  tab: RaterTab;
  transitioningAwayFromTab?: RaterTab;
  /** Tab the user last made an edit on. Used so the "Excess Rating (save first)"
   * lock does not fire for changes that originated in the Excess tab itself
   * (excess edits re-rate and mark top-level coverages hasChanged as a side
   * effect). See useRaterIsExcessTabDisabled. */
  lastEditedTab?: RaterTab;
  raterError?: string;
  raterErrorDetails?: CoverageErrorDetails;
  isAuthenticated: boolean;
  authenticationStatusChangedAt?: Date;
  expandCoverageListForExcessTowerLayerParticipants: LayerParticipantUuidTuple[];
  upsertQuoteIsLoading: boolean;
  upsertAnswersIsLoading: boolean;
  endoresementAnswers: Record<string, SaveAnswerInput[]>;
  hasSavedQuote?: boolean;
  upsertQuoteError?: string;
  upsertAnswersError?: string;
  isRequestingUpsertQuoteNow: boolean;
  isRequestingUpsertQuoteAfterRating: boolean;
  isInvalidData?: boolean;
  hasTrackingConflict?: boolean;
  inForcePolicy?: boolean;
}

type LayerUuid = string;
type ParticipantUuid = string;
type LayerParticipantUuidTuple = [LayerUuid, ParticipantUuid];

export enum RaterTab {
  CoverageRating = 'coverage-rating',
  EnterpriseRating = 'enterprise-rating',
  ExcessRating = 'excess-rating',
}

export const UNDERWRITING_RATER_TAB_NAMES = {
  [RaterTab.CoverageRating]: 'Coverage Rating',
  [RaterTab.EnterpriseRating]: 'Enterprise Rating',
  [RaterTab.ExcessRating]: 'Excess Rating',
};

export interface IUnderwritingCoverageWithDetails
  extends IUnderwritingCoverage {}

export interface IRaterEnterpriseFusionScoreProps {
  scoreTypeId: UnderwritingEnterpriseFusionScoreTypeId;
  scoreType: UnderwritingEnterpriseFusionScoreType;
  riskLevel: RiskLevelType;
  notes: string;
}
